<template>
  <VAppBar>
    <div ref="topNavLeft"></div>
    <VAppBarNavIcon
      v-if="!hideSideBar"
      @click="showNavBarComputed = !showNavBarComputed"
    />

    <VToolbarTitle class="app-bar-title">
      <div class="app-title-container">
        <span class="app-title" @click="goHome">
          <logo class="logo" />
        </span>
      </div>
    </VToolbarTitle>

    <VSpacer />

    <slot v-if="!isMobile" />

    <VSpacer />

    <SupportHardHat />
    <ChatNav v-if="hasChatPermissions" />
    <Messages v-else-if="hasMessaging" />
    <Alerts />

    <VMenu v-model="showSettings">
      <template v-slot:activator="{ props }">
        <VBtn stacked v-bind="props">
          <img
            v-if="activeProfilePicture"
            class="profile-picture"
            :src="activeProfilePicture"
          />
          <VIcon v-else>mdi-dots-vertical</VIcon>
        </VBtn>
      </template>
      <VList>
        <VListItem>
          <div class="big-profile-pic-container" v-if="activeProfilePicture">
            <img class="profile-picture big" :src="activeProfilePicture" />
          </div>

          <VListItemTitle>{{ user?.userName }}</VListItemTitle>

          <VListItemSubtitle>{{ orgName }}</VListItemSubtitle>
        </VListItem>

        <VListItem>
          <div class="profile-btn">
            <a href="/admin/user/account">
              <VBtn color="primary">{{ t('your-profile') }}</VBtn>
            </a>
          </div>
        </VListItem>

        <VDivider />

        <VListItem @click.stop>
          <VSwitch
            v-if="!isAdmin"
            v-model="theme"
            :label="t('dark-mode')"
            :true-value="'dark'"
            :false-value="'light'"
            hide-details
          />
          <v-select
            v-else
            v-model="theme"
            :items="themeItems"
            hide-details
            variant="outlined"
            density="compact"
          />
        </VListItem>

        <VListItem v-if="isDebugAdmin" @click.stop>
          <VSwitch
            v-model="debugModeOn"
            label="Debug Mode"
            hide-details
          />
        </VListItem>

        <VListItem
          v-if="isAdmin && user?.organizations?.length"
          @click.stop="showRecentClients = !showRecentClients"
        >
          <VMenu
            submenu
            open-on-hover
            :open-on-focus="false"
            activator="parent"
            v-model="showRecentClients"
          >
            <VList>
              <VListItem
                v-for="org in user.organizations"
                :key="org.orgId"
                link
                @click="changeUserClient(org)"
                :disabled="org.orgId === user.organization.orgId"
              >
                <VListItemTitle>{{ org.orgName }}</VListItemTitle>
              </VListItem>
            </VList>
          </VMenu>
          <VIcon>mdi-menu-left</VIcon>
          {{ t('recent-clients') }}
        </VListItem>

        <VListItem v-if="isAdmin" @click="startShowingChangeClient">
          {{ t('change-clients') }}
        </VListItem>

        <VListItem v-if="isAdmin" @click="settingAssumeUserComputed = true">
          {{ t('assume-user') }}
        </VListItem>

        <VListItem v-if="assumingUser" @click="stopAssumingUserHandler">
          {{ t('stop-assume-user') }}
        </VListItem>

        <VListItem
          v-if="multiClientUser"
          @click="multiOrgUserSettingOrgComputed = true"
        >
          {{ t('select-client') }}
        </VListItem>

        <VListItem @click="signOut" v-if="!onMobileApp">
          {{ t('log-out') }}
        </VListItem>
      </VList>
    </VMenu>
  </VAppBar>

  <VToolbar v-if="isMobile && hasExtraContent" floating class="second-toolbar w-100">
    <slot />
  </VToolbar>
</template>

<script setup>
import { ref, computed, toRefs, watch } from 'vue';
import Alerts from '../alerts/Alerts.vue';
import SupportHardHat from './SupportHardHat.vue';
import ChatNav from './ChatNav.vue';
import router from '../../router';
import { useRootStore } from '../../stores/root';
import { useDebugStore } from '../../stores/debug';
import { storeToRefs } from 'pinia';
import logo from '../../assets/logo.vue';
import { useDisplay, useTheme } from 'vuetify';
import changeClient from '../../helpers/changeClient';
import { useI18n } from 'vue-i18n';
import { stopAssumingUser } from '../../helpers/assumeUser';
import Messages from '../messaging/Messages.vue';
const { t } = useI18n();

const props = defineProps({
  settingAssumeUser: {
    type: Boolean,
    default: false,
  },
  showChangeClient: {
    type: Boolean,
    default: false,
  },
  changingClient: {
    type: Boolean,
    default: false,
  },
  multiOrgUserSettingOrg: {
    type: Boolean,
    default: false,
  },
  signOut: {
    type: Function,
    default: () => {},
  },
  showNavBar: {
    type: Boolean,
    default: true,
  },
  hasExtraContent: {
    type: Boolean,
    default: false,
  },
  hideSideBar: {
    type: Boolean,
    default: false,
  },
});
const emit = defineEmits([
  'update:settingAssumeUser',
  'update:changingClient',
  'update:showChangeClient',
  'update:showNavBar',
  'update:multiOrgUserSettingOrg',
]);

const showRecentClients = ref(false);
const signOut = props.signOut;
const {
  settingAssumeUser,
  changingClient,
  showChangeClient,
  hasExtraContent,
  multiOrgUserSettingOrg,
  hideSideBar,
} = toRefs(props);

const store = useRootStore();
const {
  user,
  orgName,
  isAdmin,
  isOrgAdmin,
  assumingUser,
  theme,
  org,
  permissions,
  onMobileApp,
} = storeToRefs(store);

// QsrSoft admin debugging
const debugStore = useDebugStore();
const { debugModeOn, isDebugAdmin } = storeToRefs(debugStore);

function stopAssumingUserHandler() {
  stopAssumingUser();
}

const settingAssumeUserComputed = computed({
  get() {
    return settingAssumeUser?.value;
  },
  set(value) {
    emit('update:settingAssumeUser', value);
    if (value) {
      emit('update:changingClient', false);
    }
  },
});

const hasChatPermissions = computed(() => {
  const apps = org.value?.apps ?? [];
  const chatNoCrewPortal = apps.includes('st_chat') && apps.includes('myq_ops');
  const chatCrewPortal =
    apps.includes('st_chat') && apps.includes('crew_portal');
  const hasChat = chatNoCrewPortal || chatCrewPortal;
  if (hasChat) {
    return (
      permissions.value?.includes('chat_access') ||
      isAdmin.value ||
      isOrgAdmin.value
    );
  }
  return false;
});

const hasMessaging = computed(() => {
  const orgApps = org.value?.apps || [];
  const isCommsHub = org.value?.enterpriseClient === 'CommsHub';

  return !orgApps.includes('disable_messaging') && !isCommsHub;
});

const showChangeClientComputed = computed({
  get: () => showChangeClient.value,
  set: (value) => emit('update:showChangeClient', value),
});

const changingClientComputed = computed({
  get() {
    return changingClient?.value;
  },
  set(value) {
    emit('update:changingClient', value);
    if (value) {
      emit('update:settingAssumeUser', false);
    }
  },
});

const showSettings = ref(false);
function startShowingChangeClient() {
  showChangeClientComputed.value = true;
  showSettings.value = false;
}

const { showNavBar } = toRefs(props);
const showNavBarComputed = computed({
  get() {
    return showNavBar.value;
  },
  set(value) {
    emit('update:showNavBar', value);
  },
});

const display = useDisplay();
const isMobile = display.xs;

const multiClientUser = computed(() => {
  if (isAdmin.value) return false;
  return (
    user.value?.organizations?.length > 1 || user.value?.orgIds?.length > 1
  );
});

const multiOrgUserSettingOrgComputed = computed({
  get() {
    return multiOrgUserSettingOrg.value;
  },
  set(value) {
    emit('update:multiOrgUserSettingOrg', value);
  },
});

function goHome() {
  const isCommsHub = org.value?.enterpriseClient === 'CommsHub';
  const targetPath = isCommsHub ? '/portal/commshub/home' : '/home-dashboard';

  const hasTargetRoute = router.getRoutes().some((x) => x.path === targetPath);

  if (hasTargetRoute) {
    router.push(targetPath);
  } else {
    window.location.href = targetPath;
  }
}

const themeItems = [
  { title: 'Light', value: 'light' },
  { title: 'Dark', value: 'dark' },
  { title: 'Halloween', value: 'halloween' },
  { title: 'Christmas', value: 'christmas' },
];
const vuetifyTheme = useTheme();
watch(theme, () => {
  vuetifyTheme.global.name.value = theme.value;
}, { immediate: true });

const activeProfilePicture = computed(() => {
  // check the org that the user is in right now
  const currentOrg = user.value?.organization;
  const currentOrgProfile = currentOrg?.displayData?.profilePicture?.find(
    (x) => x.active,
  );
  if (currentOrgProfile) return currentOrgProfile.url;
  const orgs = user.value?.organizations || [];
  for (const org of orgs) {
    const profile = org.displayData?.profilePicture?.find((x) => x.active);
    if (profile) return profile.url;
  }
  return null;
  // org.value?.displayData?.profilePicture?.find(x => x.active)?.url
});

async function changeUserClient(org) {
  changingClientComputed.value = true;
  await changeClient(org.orgId);
  changingClientComputed.value = false;
}

const topNavLeft = ref();
defineExpose({ topNavLeft });
</script>

<i18n>
{
  "en": {
    "your-profile": "Your Profile",
    "dark-mode": "Dark Mode",
    "recent-clients": "Recent Clients",
    "change-clients": "Change Clients",
    "assume-user": "Assume User",
    "stop-assume-user": "Stop Assume User",
    "select-client": "Select Client",
    "log-out": "Log Out"
  },
  "es": {
    "your-profile": "Mi Perfil",
    "dark-mode": "Modo Oscuro",
    "recent-clients": "Clientes recientes",
    "change-clients": "Cambiar Cliente",
    "assume-user": "Asumir Usuario",
    "stop-assume-user": "Dejar de Asumir Usuario",
    "select-client": "Seleccionar Cliente",
    "log-out": "Finalizar la Sesión"
  }
}
</i18n>

<style scoped lang="scss">
@use 'sass:map';
@use 'vuetify/settings';
$mobile-breakpoint: map.get(settings.$display-breakpoints, 'sm-and-down');
.app-bar-title {
  flex-basis: auto;
  flex-shrink: 1;
  flex-grow: 0;
  @media #{$mobile-breakpoint} {
    margin-inline-start: 0px !important;
  }
}
.app-title {
  cursor: pointer;
  user-select: none;
  height: 38px;
  @media #{$mobile-breakpoint} {
    margin-right: 0px;
  }
}
.logo {
  height: 100%;
  width: auto;
  @media #{$mobile-breakpoint} {
    width: 100% !important;
  }
}

.app-title-container {
  display: flex;
  align-items: center;
  justify-content: center;
  width: fit-content;
  gap: 0.5rem;
}

.second-toolbar {
  position: sticky;
  top: 64px;
  z-index: 1;
}

.profile-picture {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  object-fit: cover;
  &.big {
    width: 96px;
    height: 96px;
  }
}
.profile-btn {
  width: 100%;
  display: flex;
  justify-content: center;
}
.big-profile-pic-container {
  display: flex;
  justify-content: center;
}

:deep(.top-nav-icon) {
  max-width: 50px !important;
  min-width: 50px !important;
}
</style>
